interface Activity {
  timestamp: string;
  text: string;
  type?: 'normal' | 'ai-detection' | 'ai-recommendation' | 'ai-action' | 'ai-resolution';
}

const regions = [
  { name: 'China Mainland', weight: 35, regionGroup: 'Asia' },
  { name: 'Hong Kong', weight: 12, regionGroup: 'Asia' },
  { name: 'Singapore', weight: 15, regionGroup: 'Asia' },
  { name: 'Malaysia', weight: 9, regionGroup: 'Asia' },
  { name: 'Indonesia', weight: 10, regionGroup: 'Asia' },
  { name: 'Europe', weight: 15, regionGroup: 'Europe' },
  { name: 'United States', weight: 20, regionGroup: 'Americas' },
  { name: 'Middle East', weight: 5, regionGroup: 'Middle East' }
];

// Baseline date: 3 days ago
const BASELINE_DATE = new Date();
BASELINE_DATE.setDate(BASELINE_DATE.getDate() - 3);
BASELINE_DATE.setHours(0, 0, 0, 0);

// Baseline values per region (3 days ago) - in TB
export const BASELINE_REGION_DATA: Record<string, number> = {
  'China Mainland': 7665,  // 35% of 21900
  'Hong Kong': 2628,       // 12% of 21900
  'Singapore': 3285,       // 15% of 21900
  'Malaysia': 1971,        // 9% of 21900
  'Indonesia': 2190,       // 10% of 21900
  'Europe': 3285,          // 15% of 21900
  'United States': 4380,   // 20% of 21900
  'Middle East': 1095      // 5% of 21900
};

// Major global holidays (MM-DD format)
const GLOBAL_HOLIDAYS = [
  '01-01', // New Year's Day
  '12-25', // Christmas Day
];

const ASIA_HOLIDAYS = [
  '02-10', '02-11', '02-12', '02-13', '02-14', // Chinese New Year (example dates)
  '10-01', '10-02', '10-03', '10-04', '10-05', // Golden Week
];

const AMERICAS_HOLIDAYS = [
  '07-04', // Independence Day
  '11-23', // Thanksgiving (approximate)
];

const EUROPE_HOLIDAYS = [
  '04-07', // Easter (approximate)
  '12-24', '12-26', // Christmas period
];

const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6; // Sunday or Saturday
};

const isHoliday = (date: Date, region?: string): boolean => {
  const dateStr = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  
  if (GLOBAL_HOLIDAYS.includes(dateStr)) return true;
  
  if (region === 'Asia' && ASIA_HOLIDAYS.includes(dateStr)) return true;
  if (region === 'Americas' && AMERICAS_HOLIDAYS.includes(dateStr)) return true;
  if (region === 'Europe' && EUROPE_HOLIDAYS.includes(dateStr)) return true;
  
  return false;
};

const getCalendarModifier = (date: Date, region?: string): number => {
  if (isHoliday(date, region)) return 0.5; // 50% on holidays
  if (isWeekend(date)) return 0.7; // 70% on weekends
  return 1.0; // 100% on working days
};

// Calculate daily increment for a specific date
const getDailyIncrement = (date: Date): number => {
  // Use date as seed for consistent random value per day
  const seed = date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
  const pseudoRandom = (Math.sin(seed) * 10000) % 1;
  return 30 + Math.abs(pseudoRandom) * 10; // 30-40 TB per day
};

// Calculate accumulated data growth from baseline to target date
export const calculateAccumulatedGrowth = (targetDate: Date = new Date()): { total: number; byRegion: Record<string, number> } => {
  let totalGrowth = 0;
  const regionGrowth: Record<string, number> = {
    'Asia': 0,
    'Americas': 0,
    'Europe': 0,
    'Middle East': 0
  };
  
  const currentDate = new Date(BASELINE_DATE);
  currentDate.setHours(0, 0, 0, 0);
  
  const target = new Date(targetDate);
  target.setHours(0, 0, 0, 0);
  
  while (currentDate <= target) {
    const dailyIncrement = getDailyIncrement(currentDate);
    
    // Apply region-specific modifiers
    Object.keys(regionGrowth).forEach(regionGroup => {
      const modifier = getCalendarModifier(currentDate, regionGroup);
      let weight = 0;
      
      if (regionGroup === 'Asia') weight = 0.60;
      else if (regionGroup === 'Americas') weight = 0.20;
      else if (regionGroup === 'Europe') weight = 0.15;
      else if (regionGroup === 'Middle East') weight = 0.05;
      
      const regionalIncrement = dailyIncrement * modifier * weight;
      regionGrowth[regionGroup] += regionalIncrement;
      totalGrowth += regionalIncrement;
    });
    
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return { total: totalGrowth, byRegion: regionGrowth };
};

// PERSISTENCE LAYER - LocalStorage management
const STORAGE_KEY = 'jumborca_region_data';
const STORAGE_TIMESTAMP_KEY = 'jumborca_last_update';

interface PersistedRegionData {
  regions: Record<string, number>;
  timestamp: number;
}

export const loadPersistedRegionData = (): Record<string, number> | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    
    const data: PersistedRegionData = JSON.parse(stored);
    return data.regions;
  } catch {
    return null;
  }
};

export const saveRegionData = (regionData: Record<string, number>): void => {
  try {
    const data: PersistedRegionData = {
      regions: regionData,
      timestamp: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    localStorage.setItem(STORAGE_TIMESTAMP_KEY, Date.now().toString());
  } catch {
    // Silent fail if localStorage is unavailable
  }
};

export const getLastUpdateTimestamp = (): number => {
  try {
    const stored = localStorage.getItem(STORAGE_TIMESTAMP_KEY);
    return stored ? parseInt(stored, 10) : Date.now();
  } catch {
    return Date.now();
  }
};

// Calculate catch-up growth for missed ticks
export const calculateCatchUpGrowth = (lastTimestamp: number): Record<string, number> => {
  const now = Date.now();
  const elapsedMs = now - lastTimestamp;
  const missedTicks = Math.floor(elapsedMs / 30000); // 30 seconds per tick
  
  if (missedTicks <= 0) {
    return {};
  }
  
  // Calculate growth per tick (distributed by weights)
  const growthPerTick = 0.5; // Base growth per tick in TB
  const catchUpGrowth: Record<string, number> = {};
  
  // Regional group weights
  const groupWeights = {
    'Asia': 0.60,
    'Americas': 0.20,
    'Europe': 0.15,
    'Middle East': 0.05
  };
  
  // Within Asia distribution
  const asiaDistribution: Record<string, number> = {
    'China Mainland': 0.40,
    'Singapore': 0.20,
    'Hong Kong': 0.15,
    'Malaysia': 0.12,
    'Indonesia': 0.13
  };
  
  regions.forEach(region => {
    let regionShare = 0;
    
    if (region.regionGroup === 'Asia') {
      const asiaShare = groupWeights['Asia'];
      const withinAsiaShare = asiaDistribution[region.name] || 0;
      regionShare = asiaShare * withinAsiaShare;
    } else {
      const groupShare = groupWeights[region.regionGroup as keyof typeof groupWeights] || 0;
      const regionGroupWeight = regions
        .filter(r => r.regionGroup === region.regionGroup)
        .reduce((sum, r) => sum + r.weight, 0);
      regionShare = groupShare * (region.weight / regionGroupWeight);
    }
    
    catchUpGrowth[region.name] = growthPerTick * regionShare * missedTicks;
  });
  
  return catchUpGrowth;
};

// Initialize or load region data with catch-up
export const initializeRegionData = (): Record<string, number> => {
  const persisted = loadPersistedRegionData();
  const lastUpdate = getLastUpdateTimestamp();
  
  if (persisted) {
    // Apply catch-up growth for missed ticks
    const catchUp = calculateCatchUpGrowth(lastUpdate);
    const updated: Record<string, number> = {};
    
    Object.keys(persisted).forEach(regionName => {
      const catchUpAmount = catchUp[regionName] || 0;
      updated[regionName] = persisted[regionName] + catchUpAmount;
    });
    
    saveRegionData(updated);
    return updated;
  }
  
  // First time initialization - calculate from baseline
  const growth = calculateAccumulatedGrowth();
  const regionData: Record<string, number> = {};
  
  regions.forEach(region => {
    const baseline = BASELINE_REGION_DATA[region.name];
    const groupGrowth = growth.byRegion[region.regionGroup];
    
    const regionGroupWeight = regions
      .filter(r => r.regionGroup === region.regionGroup)
      .reduce((sum, r) => sum + r.weight, 0);
    
    const regionShare = region.weight / regionGroupWeight;
    const regionGrowth = groupGrowth * regionShare;
    
    regionData[region.name] = baseline + regionGrowth;
  });
  
  saveRegionData(regionData);
  return regionData;
};

// Apply single tick growth (called every 30 seconds)
export const applyTickGrowth = (currentData: Record<string, number>): Record<string, number> => {
  const growthPerTick = 0.5; // Base growth per tick in TB
  const updated: Record<string, number> = {};
  
  // Regional group weights
  const groupWeights = {
    'Asia': 0.60,
    'Americas': 0.20,
    'Europe': 0.15,
    'Middle East': 0.05
  };
  
  // Within Asia distribution
  const asiaDistribution: Record<string, number> = {
    'China Mainland': 0.40,
    'Singapore': 0.20,
    'Hong Kong': 0.15,
    'Malaysia': 0.12,
    'Indonesia': 0.13
  };
  
  regions.forEach(region => {
    let regionShare = 0;
    
    if (region.regionGroup === 'Asia') {
      const asiaShare = groupWeights['Asia'];
      const withinAsiaShare = asiaDistribution[region.name] || 0;
      regionShare = asiaShare * withinAsiaShare;
    } else {
      const groupShare = groupWeights[region.regionGroup as keyof typeof groupWeights] || 0;
      const regionGroupWeight = regions
        .filter(r => r.regionGroup === region.regionGroup)
        .reduce((sum, r) => sum + r.weight, 0);
      regionShare = groupShare * (region.weight / regionGroupWeight);
    }
    
    const increment = growthPerTick * regionShare;
    const minIncrement = 0.1; // Ensure at least 0.1 TB growth per tick
    const finalIncrement = Math.max(increment, minIncrement);
    
    // Monotonic guard: never decrease
    updated[region.name] = Math.max(
      currentData[region.name] || 0,
      (currentData[region.name] || 0) + finalIncrement
    );
  });
  
  saveRegionData(updated);
  return updated;
};

// Calculate current region data values (for backward compatibility)
export const calculateRegionData = (): Record<string, number> => {
  return initializeRegionData();
};

// Calculate total protected data (sum of all regions)
export const calculateTotalProtectedData = (regionData?: Record<string, number>): number => {
  const data = regionData || initializeRegionData();
  return Object.values(data).reduce((sum, value) => sum + value, 0);
};

// Calculate immutable copies (derived from total protected data)
// Rule: For each 1 TB of protected data, there are approximately 2-4 immutable copies
export const calculateImmutableCopies = (totalProtectedDataTB: number): number => {
  const baseMultiplier = 3.0; // Stable value (between 2-4)
  return totalProtectedDataTB * baseMultiplier;
};

const generateTimestamp = (hoursAgo: number): string => {
  const date = new Date();
  date.setHours(date.getHours() - hoursAgo);
  return date.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false });
};

const getWeightedRegion = (): string => {
  const totalWeight = regions.reduce((sum, r) => sum + r.weight, 0);
  let random = Math.random() * totalWeight;
  
  for (const region of regions) {
    random -= region.weight;
    if (random <= 0) {
      return region.name;
    }
  }
  return regions[0].name;
};

const generateActivity = (): Activity[] => {
  const activities: Activity[] = [];
  const now = new Date();
  
  // Generate activities for the last 48 hours
  for (let i = 0; i < 300; i++) {
    const rand = Math.random();
    const region = getWeightedRegion();
    const hoursAgo = Math.random() * 48;
    
    const activityDate = new Date(now.getTime() - hoursAgo * 60 * 60 * 1000);
    const regionData = regions.find(r => r.name === region);
    const calendarMod = getCalendarModifier(activityDate, regionData?.regionGroup);
    
    // AI-driven events (5% of total activities - infrequent but credible)
    if (rand < 0.015) {
      // AI Detection (1.5%)
      const detectionTypes = [
        'abnormal backup delay',
        'SLA risk pattern',
        'network throughput anomaly',
        'backup window congestion',
        'resource contention pattern'
      ];
      const detectionType = detectionTypes[Math.floor(Math.random() * detectionTypes.length)];
      activities.push({
        timestamp: generateTimestamp(hoursAgo),
        text: `AI detected ${detectionType} | ${region}`,
        type: 'ai-detection'
      });
    } else if (rand < 0.025) {
      // AI Recommendation (1%)
      const recommendations = [
        'backup window rebalancing',
        'cross-region retry',
        'resource reallocation',
        'priority queue adjustment',
        'bandwidth optimization'
      ];
      const recommendation = recommendations[Math.floor(Math.random() * recommendations.length)];
      activities.push({
        timestamp: generateTimestamp(hoursAgo),
        text: `AI recommended ${recommendation} | ${region}`,
        type: 'ai-recommendation'
      });
    } else if (rand < 0.035) {
      // AI Action & Resolution (1%)
      const actions = [
        { action: 'auto-adjustment applied', resolution: 'SLA risk mitigated' },
        { action: 'initiated retry succeeded', resolution: 'backup completed' },
        { action: 'rebalanced backup window', resolution: 'congestion resolved' },
        { action: 'optimized network path', resolution: 'throughput restored' },
        { action: 'reallocated resources', resolution: 'contention eliminated' }
      ];
      const selectedAction = actions[Math.floor(Math.random() * actions.length)];
      activities.push({
        timestamp: generateTimestamp(hoursAgo),
        text: `AI ${selectedAction.action} | ${region}`,
        type: 'ai-action'
      });
      // Add resolution event shortly after
      activities.push({
        timestamp: generateTimestamp(hoursAgo - 0.1),
        text: `${selectedAction.resolution} | ${region}`,
        type: 'ai-resolution'
      });
    } else if (rand < 0.55) {
      // BACKUP_INCREASE (51.5%)
      let volume: number;
      if (region === 'China Mainland' || region === 'United States' || region === 'Singapore') {
        volume = Math.floor(Math.random() * 115 * calendarMod) + 5;
      } else if (region === 'Middle East') {
        volume = Math.floor(Math.random() * 30 * calendarMod) + 5;
      } else {
        volume = Math.floor(Math.random() * 80 * calendarMod) + 10;
      }
      activities.push({
        timestamp: generateTimestamp(hoursAgo),
        text: `+${volume} TB Backup Completed | ${region} Region`,
        type: 'normal'
      });
    } else if (rand < 0.80) {
      // DATA_RESTORED (25%)
      let volume: number;
      if (region === 'Middle East') {
        volume = Math.floor(Math.random() * 15) + 1;
      } else if (region === 'China Mainland' || region === 'United States') {
        volume = Math.floor(Math.random() * 45) + 5;
      } else {
        volume = Math.floor(Math.random() * 30) + 2;
      }
      activities.push({
        timestamp: generateTimestamp(hoursAgo),
        text: `Data Restored Successfully | ${region} Region | ${volume} TB`,
        type: 'normal'
      });
    } else {
      // INTEGRITY_VERIFIED (20%)
      let volume: number;
      if (region === 'China Mainland') {
        volume = Math.floor(Math.random() * 180) + 120;
      } else if (region === 'United States' || region === 'Singapore') {
        volume = Math.floor(Math.random() * 140) + 80;
      } else if (region === 'Middle East') {
        volume = Math.floor(Math.random() * 40) + 10;
      } else {
        volume = Math.floor(Math.random() * 100) + 50;
      }
      activities.push({
        timestamp: generateTimestamp(hoursAgo),
        text: `Immutable Data Integrity Verified | ${region} Region | ${volume} TB`,
        type: 'normal'
      });
    }
  }
  
  return activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};

export const activityData = generateActivity();
